import React, { useState } from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Input as InputComponent } from '@/components/ui/input'
import { eventLabel, maximumToMinor } from '@/utils/pixel-warmup'
import { STEP_LABELS, SKIP_REASON_TEXT, pendingReasonText } from '@/utils/pixel-warmup-panel'

const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>
const Button = ButtonComponent as React.ComponentType<any>

export interface HeldRow {
    id: string
    name: string
    budget: number
    status: string | null
    external_id: string | null
    held: boolean
    step: string | null
    skip_reason: string | null
    release_reason: string | null
    pending_reason: string | null
    event: string | null
    /** The server allows this row's budget and event to be edited now. */
    can_edit: boolean
    /** The server accepts a budget change of this row now (also for a row that is already on Meta). */
    can_edit_budget: boolean
}

interface WarmupHeldRowsProps {
    rows: HeldRow[]
    /** An expansion is running: no edit is possible until it has finished. */
    locked: boolean
    /** The owner may remove held ad sets now. */
    canRemove: boolean
    /** The owner may ask Meta again about an ad set. */
    canRecheck: boolean
    busy: boolean
    formatAmount: (minor: number) => string
    editHref: (row: HeldRow) => string
    onSaveBudget: (adSetId: string, budget: string, expectedMinor: number) => void
    onRemove: (row: HeldRow) => void
    onRecheck: (adSetId: string) => void
    onEdit: (href: string) => void
}

const EVENT_KEPT_NOTE =
    'Already created on Meta (paused): its event is kept. To change the event, remove it and add the audience after the warm-up.'

const stepText = (row: HeldRow) => {
    if (row.skip_reason) return (SKIP_REASON_TEXT as Record<string, string>)[row.skip_reason] || 'Skipped'
    return (
        (row.step && (STEP_LABELS as Record<string, string>)[row.step]) ||
        (row.held ? 'Held back until the expansion' : row.status || '')
    )
}

/** One audience ad set of the warm-up, with the edits its stage allows. */
const HeldRowItem: React.FC<{ row: HeldRow } & Omit<WarmupHeldRowsProps, 'rows'>> = ({
    row,
    locked,
    canRemove,
    canRecheck,
    busy,
    formatAmount,
    editHref,
    onSaveBudget,
    onRemove,
    onRecheck,
    onEdit,
}) => {
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState(String(row.budget / 100))
    const [problem, setProblem] = useState<string | null>(null)
    const editable = row.can_edit
    const budgetEditable = row.can_edit_budget
    const created = !!row.external_id

    return (
        <li className="tw:flex tw:flex-col tw:gap-1 tw:border-b tw:border-[#2B3B4C] tw:py-2" data-testid="held-row">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <span className="tw:font-medium">{row.name}</span>
                {editing ? (
                    <span className="tw:flex tw:items-center tw:gap-2">
                        <Input
                            aria-label={`Daily budget of ${row.name}`}
                            className="tw:w-28"
                            inputMode="decimal"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                        <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => {
                                if (maximumToMinor(value) === null) {
                                    setProblem(
                                        'Enter a daily budget as a number greater than zero, for example 50 or 50.25.',
                                    )
                                    return
                                }
                                setProblem(null)
                                onSaveBudget(row.id, value, row.budget)
                                setEditing(false)
                            }}
                        >
                            Save
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                            Cancel
                        </Button>
                    </span>
                ) : (
                    <span data-testid="held-row-budget">{formatAmount(row.budget)} a day</span>
                )}
                <span className="tw:text-gray-200">{stepText(row)}</span>
                {problem && (
                    <span className="tw:text-yellow-300" role="alert" data-testid="held-row-problem">
                        {problem}
                    </span>
                )}
                {row.event && (
                    <span className="tw:text-gray-200" data-testid="held-row-event">
                        Event: {eventLabel(row.event)}
                    </span>
                )}
                {row.pending_reason && (
                    <span className="tw:text-yellow-300" data-testid="held-row-pending">
                        {pendingReasonText(row.pending_reason)}
                    </span>
                )}
            </div>
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:text-xs">
                {budgetEditable && !editing && (
                    <button
                        type="button"
                        className="tw:text-blue-400 tw:hover:underline"
                        onClick={() => setEditing(true)}
                    >
                        Change budget
                    </button>
                )}
                {editable && !created && (
                    <button
                        type="button"
                        className="tw:text-blue-400 tw:hover:underline"
                        onClick={() => onEdit(editHref(row))}
                    >
                        Edit event and settings
                    </button>
                )}
                {row.held && created && (
                    <span className="tw:text-gray-200" data-testid="held-row-event-kept">
                        {EVENT_KEPT_NOTE}
                    </span>
                )}
                {row.held && !locked && canRecheck && created && (
                    <button
                        type="button"
                        className="tw:text-blue-400 tw:hover:underline"
                        disabled={busy}
                        onClick={() => onRecheck(row.id)}
                    >
                        Check again
                    </button>
                )}
                {canRemove && row.held && (
                    <button
                        type="button"
                        className="tw:text-red-300 tw:hover:underline"
                        disabled={busy}
                        onClick={() => onRemove(row)}
                    >
                        Remove
                    </button>
                )}
                {row.held && locked && (
                    <span className="tw:text-gray-200">An expansion is running; try again when it has finished.</span>
                )}
            </div>
        </li>
    )
}

/** The audience ad sets the warm-up holds back, as the Ads Manager list does not show them before they exist. */
const WarmupHeldRows: React.FC<WarmupHeldRowsProps> = ({ rows, ...rest }) => {
    if (rows.length === 0) return null
    return (
        <div data-testid="warmup-held-rows">
            <h3 className="tw:!m-0 tw:!mb-1 tw:!text-sm tw:!font-semibold">Audience ad sets of the warm-up</h3>
            <ul className="tw:!m-0 tw:!p-0 tw:list-none tw:text-sm">
                {rows.map((row) => (
                    <HeldRowItem key={row.id} row={row} {...rest} />
                ))}
            </ul>
        </div>
    )
}

export default WarmupHeldRows
